import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Checkbox,
  EmptyState,
  Input,
  Numeric,
  Select,
  StatusPill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type Density,
  type Tone,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { formatGBP } from "../lib/format.js";
import { useToast } from "../lib/toast.js";
import type {
  InvoiceStatus,
  InvoicesListResponseDTO,
  InvoiceSummaryDTO,
} from "@wmart/shared-types";

const STATUS_OPTIONS: Array<{ value: InvoiceStatus | "all"; label: string }> = [
  { value: "all", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "issued", label: "Issued" },
  { value: "sent", label: "Sent" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
  { value: "void", label: "Void" },
];

const STATUS_TONE: Record<InvoiceStatus, Tone> = {
  draft: "neutral",
  issued: "info",
  sent: "info",
  paid: "success",
  overdue: "danger",
  void: "neutral",
};

function formatLondonDate(iso: string | null): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(iso));
}

/**
 * Admin invoices list (P15-T10).
 *
 * Filters: status, company id (uuid input — matches OrdersList's pragmatic
 * "type a customer id" pattern from V1.0; richer pickers are V2). Free-text
 * search by invoice number is client-side over the loaded page — sufficient
 * for the 20-row pages the API returns, no server-side q param exists yet.
 *
 * Multi-select for merge: checkboxes appear next to each `draft` row. The
 * merge call only accepts ≥2 draft invoices for the same company/customer
 * (server enforces); the toolbar enables the "Merge selected" CTA when the
 * selection meets the size guard. Cross-status / cross-company picks are
 * rejected by the server with a clear 422.
 */
export function InvoicesList() {
  const navigate = useNavigate();
  const { notify, notifyProblem } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<InvoiceSummaryDTO[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [density, setDensity] = useState<Density>("default");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | "all">(
    () => {
      const s = searchParams.get("status");
      const ok = STATUS_OPTIONS.some((o) => o.value === s);
      return ok ? (s as InvoiceStatus | "all") : "all";
    },
  );
  const [companyId, setCompanyId] = useState(searchParams.get("companyId") ?? "");
  const [debouncedCompanyId, setDebouncedCompanyId] = useState(companyId);
  const [q, setQ] = useState(searchParams.get("q") ?? "");
  const [debouncedQ, setDebouncedQ] = useState(q);

  // Density (carry-forward from OrdersList).
  useEffect(() => {
    const stored = window.localStorage.getItem("wmart.density");
    if (stored === "compact" || stored === "default" || stored === "cosy") {
      setDensity(stored);
    }
  }, []);

  // Debounce text inputs (250ms — parity with CompaniesList).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedCompanyId(companyId.trim()), 250);
    return () => clearTimeout(t);
  }, [companyId]);

  async function fetchPage(reset: boolean, cursor: string | null) {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "50");
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (debouncedCompanyId) params.set("companyId", debouncedCompanyId);
      if (cursor) params.set("cursor", cursor);
      const data = await api.get<InvoicesListResponseDTO>(
        `/v1/admin/invoices?${params.toString()}`,
      );
      setItems((prev) =>
        reset ? data.invoices : [...prev, ...data.invoices],
      );
      setNextCursor(data.pagination.nextCursor);
      if (reset) {
        // Clear stale selections on filter changes.
        setSelectedIds(new Set());
      }
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void fetchPage(true, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, debouncedCompanyId]);

  // Keep URL in sync with active filters so deep-links round-trip.
  useEffect(() => {
    const next = new URLSearchParams();
    if (statusFilter !== "all") next.set("status", statusFilter);
    if (debouncedCompanyId) next.set("companyId", debouncedCompanyId);
    if (debouncedQ.trim()) next.set("q", debouncedQ.trim());
    setSearchParams(next, { replace: true });
  }, [statusFilter, debouncedCompanyId, debouncedQ, setSearchParams]);

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Filter the in-memory page by free-text number match. Cheap and matches
  // operator expectations ("type the last 4 digits"); the API is paginated
  // so this only ever sees the loaded slice — Load more reveals more.
  const filteredItems = debouncedQ.trim()
    ? items.filter((inv) =>
        inv.number.toLowerCase().includes(debouncedQ.trim().toLowerCase()),
      )
    : items;

  const draftSelectable = filteredItems.filter((i) => i.status === "draft");
  const selectedCount = selectedIds.size;
  const canMerge = selectedCount >= 2;
  const hasAnyFilter =
    statusFilter !== "all" ||
    debouncedCompanyId !== "" ||
    debouncedQ.trim() !== "";

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-display-sm text-ink-900">Invoices</h1>
        {canMerge ? (
          <Button
            onClick={() =>
              navigate(
                `/invoices/merge?ids=${Array.from(selectedIds).join(",")}`,
              )
            }
          >
            Merge {selectedCount} drafts
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <Input
            type="search"
            label="Search number"
            placeholder="e.g. 2026-000123"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="w-48">
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as InvoiceStatus | "all")
            }
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-72">
          <Input
            type="text"
            label="Company id"
            placeholder="uuid"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
          />
        </div>
        {hasAnyFilter ? (
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              setStatusFilter("all");
              setCompanyId("");
              setQ("");
            }}
          >
            Clear
          </Button>
        ) : null}
      </div>

      {filteredItems.length === 0 && !loading ? (
        <EmptyState
          icon="package"
          title="No invoices match"
          body="Adjust filters to see more results."
        />
      ) : (
        <>
          <Table density={density}>
            <THead>
              <TR>
                <TH style={{ width: 32 }} aria-label="Select">
                  {draftSelectable.length > 0 ? (
                    <span className="sr-only">Select draft invoices to merge</span>
                  ) : null}
                </TH>
                <TH>Number</TH>
                <TH>Status</TH>
                <TH>Issued</TH>
                <TH>Due</TH>
                <TH className="text-right">Total</TH>
                <TH>PDF</TH>
              </TR>
            </THead>
            <TBody>
              {filteredItems.map((inv) => {
                const isDraft = inv.status === "draft";
                const checked = selectedIds.has(inv.id);
                return (
                  <TR key={inv.id}>
                    <TD>
                      {isDraft ? (
                        <Checkbox
                          aria-label={`Select draft ${inv.number}`}
                          checked={checked}
                          onChange={() => toggleSelect(inv.id)}
                        />
                      ) : null}
                    </TD>
                    <TD className="font-mono">
                      <Link
                        to={`/invoices/${inv.id}`}
                        className="text-ink-900 hover:underline"
                      >
                        {inv.number}
                      </Link>
                    </TD>
                    <TD>
                      <StatusPill tone={STATUS_TONE[inv.status]}>
                        {inv.status}
                      </StatusPill>
                    </TD>
                    <TD className="text-body-sm text-ink-500">
                      {formatLondonDate(inv.issuedAt)}
                    </TD>
                    <TD className="text-body-sm text-ink-500">
                      {inv.dueDate ?? "—"}
                    </TD>
                    <TD className="text-right">
                      <Numeric serif>{formatGBP(inv.totalMinor)}</Numeric>
                    </TD>
                    <TD>
                      {/* No /v1/admin/invoices/:id/pdf endpoint exists today
                          (V1.5 PDF delivery is buyer-only via the storefront
                          route). Surface a yes/no marker so operators can see
                          which invoices have a rendered PDF available for the
                          buyer — useful when chasing dunning state. */}
                      {inv.hasPdf ? (
                        <span className="text-body-sm text-ink-700">Yes</span>
                      ) : (
                        <span className="text-body-sm text-ink-400">—</span>
                      )}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>

          {nextCursor ? (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                onClick={() => void fetchPage(false, nextCursor)}
                disabled={loading}
                loading={loading}
              >
                Load more
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
